import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Search, Trash2, X, CheckCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatDate, daysUntilExpiry, formatPrice, formatPriceInput, parsePriceInput } from '../utils/helpers';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';

function addMonths(dateStr, months) {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

const AssignLockerForm = ({ locker, onClose, onDelete }) => {
  const { members, trainers, updateLocker, gymSettings } = useApp();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const lockerCfg = gymSettings.lockerSettings ?? { enabled: false, isFree: true, monthlyFee: 0, periods: [1, 3, 6, 12] };
  const useSettings = lockerCfg.enabled;

  const [search, setSearch] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState(locker.memberId || '');
  const [password, setPassword] = useState(locker.password || '');
  const [deposit, setDeposit] = useState(locker.deposit ? String(locker.deposit) : '');
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(locker.startDate || today);
  const [endDate, setEndDate] = useState(locker.endDate || '');
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const fee = useMemo(() => {
    if (!useSettings || lockerCfg.isFree || !selectedPeriod) return null;
    return lockerCfg.monthlyFee * selectedPeriod;
  }, [useSettings, lockerCfg.isFree, lockerCfg.monthlyFee, selectedPeriod]);

  function handlePeriodSelect(months) {
    setSelectedPeriod(months);
    setEndDate(addMonths(startDate, months));
  }

  // 회원 + 트레이너 통합 검색 목록
  const allPeople = [
    ...members.map(m => ({ ...m, _type: '회원' })),
    ...(trainers || []).map(t => ({ ...t, _type: '트레이너' })),
  ];
  const filtered = allPeople.filter(m =>
    !search || m.name?.includes(search) || m.phone?.includes(search)
  );

  const selectedPerson = allPeople.find(m => m.id === selectedMemberId);
  const isTrainer = selectedPerson?._type === '트레이너';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedMemberId && useSettings && !endDate) return;
    updateLocker(locker.id, {
      memberId: selectedMemberId || null,
      memberName: selectedPerson?.name || null,
      assigneeType: isTrainer ? 'trainer' : 'member',
      startDate: selectedMemberId ? startDate : null,
      endDate: selectedMemberId ? endDate : null,
      status: selectedMemberId ? 'occupied' : 'empty',
      password: password || null,
      deposit: deposit ? Number(parsePriceInput(deposit)) : 0,
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center py-2">
        <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-2">
          <span className="text-2xl font-bold text-gray-600 dark:text-gray-300">{locker.number}</span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">락커 번호 {locker.number}</p>
      </div>

      <div>
        <label className="label">배정 대상 (회원/트레이너, 비우려면 미선택)</label>
        {selectedPerson && (
          <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedPerson.name} ({selectedPerson.phone})
              <span className="ml-1.5 text-xs opacity-60">{selectedPerson._type}</span>
            </span>
            <button type="button" onClick={() => setSelectedMemberId('')} className="text-xs text-gray-400 hover:text-red-500">✕</button>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="회원/트레이너 이름 또는 전화번호 검색..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {search && (
          <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-3">검색 결과가 없습니다.</p>
            ) : (
              filtered.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { setSelectedMemberId(m.id); setSearch(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{m.name}</span>
                  <span className="text-gray-400 ml-2">{m.phone}</span>
                  <span className="ml-1.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{m._type}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selectedMemberId && (
        useSettings ? (
          <div className="space-y-3">
            <div>
              <label className="label">시작일</label>
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  if (selectedPeriod) setEndDate(addMonths(e.target.value, selectedPeriod));
                }}
              />
            </div>
            <div>
              <label className="label">기간 선택</label>
              <div className="flex gap-2 flex-wrap">
                {(lockerCfg.periods || []).map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handlePeriodSelect(m)}
                    className={`px-4 py-1.5 rounded-xl border text-sm font-medium transition-colors ${
                      selectedPeriod === m
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {m}개월
                  </button>
                ))}
              </div>
            </div>
            {endDate && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">만료일</span>
                  <span className="font-medium text-gray-900 dark:text-white">{endDate}</span>
                </div>
                {fee !== null && (
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500 dark:text-gray-400">요금</span>
                    <span className="font-bold text-primary-600 dark:text-primary-400">{fee.toLocaleString('ko-KR')}원</span>
                  </div>
                )}
                {lockerCfg.isFree && (
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500 dark:text-gray-400">요금</span>
                    <span className="font-medium text-green-600 dark:text-green-400">무료</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">시작일</label>
              <input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} required />
            </div>
            <div>
              <label className="label">만료일</label>
              <input type="date" className="input" value={endDate} onChange={e => setEndDate(e.target.value)} required />
            </div>
          </div>
        )
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">락커 비밀번호 (선택)</label>
          <input
            type="text"
            className="input"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="비밀번호"
            maxLength={20}
          />
        </div>
        <div>
          <label className="label">보증금 (원, 선택)</label>
          <input
            type="text"
            inputMode="numeric"
            className="input"
            value={formatPriceInput(deposit)}
            onChange={e => setDeposit(parsePriceInput(e.target.value))}
            placeholder="0"
          />
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={() => setDeleteConfirm(true)}
          className="text-sm text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
        >
          락커 삭제
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-secondary">취소</button>
          <button type="submit" className="btn-primary">저장</button>
        </div>
      </div>
      <ConfirmDialog
        isOpen={deleteConfirm}
        title="락커 삭제"
        message={`${locker.number}번 락커를 삭제하시겠습니까?`}
        onConfirm={() => { onDelete(locker.id); onClose(); }}
        onCancel={() => setDeleteConfirm(false)}
      />
    </form>
  );
};

const Lockers = () => {
  const { lockers, addLockers, deleteLocker } = useApp();
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [isAddingLocker, setIsAddingLocker] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // 다중 삭제 모드
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        await deleteLocker(id);
      }
    } finally {
      setBulkDeleting(false);
      setSelectedIds(new Set());
      setDeleteMode(false);
    }
  };

  const exitDeleteMode = () => {
    setDeleteMode(false);
    setSelectedIds(new Set());
  };

  const occupied = lockers.filter(l => l.status === 'occupied').length;
  const empty = lockers.filter(l => l.status === 'empty').length;

  const handleAddLockers = async (count) => {
    setAddingLoading(true);
    try {
      const maxNum = lockers.reduce((max, l) => Math.max(max, l.number), 0);
      const newLockers = Array.from({ length: count }, (_, i) => ({
        number: maxNum + i + 1,
        memberId: null,
        memberName: null,
        startDate: null,
        endDate: null,
        status: 'empty',
        deposit: 0,
        assigneeType: 'member',
      }));
      await addLockers(newLockers);
    } finally {
      setAddingLoading(false);
      setIsAddingLocker(false);
    }
  };

  const occupiedLockers = useMemo(() => {
    const list = lockers.filter(l => l.status === 'occupied').sort((a, b) => a.number - b.number);
    if (!listSearch) return list;
    const q = listSearch.toLowerCase();
    return list.filter(l => l.memberName?.toLowerCase().includes(q) || String(l.number).includes(q));
  }, [lockers, listSearch]);

  return (
    <div className="space-y-4">
      {/* 현황 요약 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{lockers.length}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">전체</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{occupied}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">사용중</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">{empty}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">비어있음</p>
        </div>
      </div>

      {/* 락커 추가/삭제 버튼 */}
      <div className="flex justify-end gap-2">
        {deleteMode ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedIds.size > 0
                ? <><span className="font-semibold text-red-500">{selectedIds.size}개</span> 선택됨</>
                : '삭제할 락커를 선택하세요'}
            </span>
            <button
              onClick={() => {
                const occupiedCount = [...selectedIds].filter(id => lockers.find(l => l.id === id)?.status === 'occupied').length;
                setDeleteConfirm({ bulk: true, count: selectedIds.size, occupiedCount });
              }}
              disabled={selectedIds.size === 0 || bulkDeleting}
              className="btn-primary !bg-red-600 hover:!bg-red-700 flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {bulkDeleting ? '삭제 중...' : `${selectedIds.size}개 삭제`}
            </button>
            <button onClick={exitDeleteMode} className="btn-secondary flex items-center gap-1.5" disabled={bulkDeleting}>
              <X className="w-3.5 h-3.5" />
              취소
            </button>
          </div>
        ) : isAddingLocker ? (
          <div className="flex gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400 self-center">{addingLoading ? '추가 중...' : '추가할 락커 수:'}</span>
            {[5, 10, 20].map(n => (
              <button key={n} onClick={() => handleAddLockers(n)} className="btn-secondary" disabled={addingLoading}>{n}개</button>
            ))}
            <button onClick={() => setIsAddingLocker(false)} className="btn-secondary" disabled={addingLoading}>취소</button>
          </div>
        ) : (
          <>
            {lockers.length > 0 && (
              <button onClick={() => setDeleteMode(true)} className="btn-secondary flex items-center gap-1.5 text-red-500 hover:text-red-700 border-red-200 dark:border-red-800 hover:border-red-400">
                <Trash2 className="w-4 h-4" />
                락커 삭제
              </button>
            )}
            <button onClick={() => setIsAddingLocker(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              락커 추가
            </button>
          </>
        )}
      </div>

      {/* 락커 그리드 */}
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
        {[...lockers].sort((a, b) => a.number - b.number).map(locker => {
          const days = locker.endDate ? daysUntilExpiry(locker.endDate) : null;
          const isExpiringSoon = days !== null && days >= 0 && days <= 7;
          const isExpired = days !== null && days < 0;
          const isSelected = deleteMode && selectedIds.has(locker.id);
          return (
            <button
              key={locker.id}
              onClick={() => deleteMode ? toggleSelect(locker.id) : setSelectedLocker(locker)}
              className={`aspect-square rounded-xl flex flex-col items-center justify-center p-1 border-2 transition-all hover:scale-105 relative ${
                isSelected
                  ? 'bg-red-100 dark:bg-red-900/30 border-red-500 dark:border-red-500 ring-2 ring-red-300 dark:ring-red-700'
                  : locker.status === 'empty'
                    ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-400'
                    : isExpired
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                      : isExpiringSoon
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
              }`}
              title={deleteMode ? (isSelected ? '선택 해제' : '삭제 선택') : (locker.memberName ? `${locker.memberName} (${formatDate(locker.endDate)})` : '비어있음')}
            >
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                  <CheckCircle className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <span className={`text-xs font-bold ${
                isSelected ? 'text-red-600 dark:text-red-400' :
                locker.status === 'empty' ? 'text-gray-500 dark:text-gray-400' :
                isExpired ? 'text-red-600 dark:text-red-400' :
                isExpiringSoon ? 'text-yellow-600 dark:text-yellow-400' :
                'text-blue-700 dark:text-blue-300'
              }`}>{locker.number}</span>
              {locker.memberName && !deleteMode && (
                <span className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 truncate w-full text-center px-0.5">
                  {locker.memberName.length > 3 ? locker.memberName.slice(0, 3) : locker.memberName}
                </span>
              )}
              {deleteMode && locker.status === 'occupied' && (
                <span className="text-[8px] text-orange-500 dark:text-orange-400 mt-0.5 font-medium">사용중</span>
              )}
            </button>
          );
        })}
      </div>

      {/* 범례 */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700" />비어있음</div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700" />사용중</div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700" />만료임박 (7일)</div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700" />만료</div>
      </div>

      {/* 목록 뷰 */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <h3 className="font-semibold text-gray-900 dark:text-white flex-shrink-0">사용중인 락커</h3>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="이름 또는 번호 검색"
              value={listSearch}
              onChange={e => setListSearch(e.target.value)}
              className="input pl-8 py-1.5 text-sm"
            />
          </div>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {occupiedLockers.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-sm">
              {listSearch ? '검색 결과가 없습니다.' : '사용중인 락커가 없습니다.'}
            </p>
          ) : (
            occupiedLockers.map(locker => {
              const days = daysUntilExpiry(locker.endDate);
              return (
                <div key={locker.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-blue-700 dark:text-blue-400 text-sm">{locker.number}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {locker.memberName}
                        {locker.assigneeType === 'trainer' && (
                          <span className="ml-1.5 text-xs text-blue-500 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">트레이너</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">{formatDate(locker.startDate)} ~ {formatDate(locker.endDate)}</p>
                      <div className="flex gap-3 text-xs text-gray-400 mt-0.5">
                        {locker.password && (
                          <span>비번: <span className="font-mono text-gray-600 dark:text-gray-300">{locker.password}</span></span>
                        )}
                        {locker.deposit > 0 && (
                          <span>보증금: <span className="font-medium text-gray-600 dark:text-gray-300">{formatPrice(locker.deposit)}</span></span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {days !== null && (
                      <span className={`text-xs font-semibold ${days < 0 ? 'text-red-500' : days <= 7 ? 'text-yellow-500' : 'text-gray-400'}`}>
                        {days < 0 ? '만료됨' : `D-${days}`}
                      </span>
                    )}
                    <button onClick={() => setSelectedLocker(locker)} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">관리</button>
                    <button
                      onClick={() => setDeleteConfirm(locker)}
                      className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                      title="락커 삭제"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <Modal isOpen={!!selectedLocker} onClose={() => setSelectedLocker(null)} title="락커 관리">
        {selectedLocker && <AssignLockerForm locker={selectedLocker} onClose={() => setSelectedLocker(null)} onDelete={(id) => { deleteLocker(id); setSelectedLocker(null); }} />}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title={deleteConfirm?.bulk ? '락커 일괄 삭제' : '락커 삭제'}
        message={deleteConfirm?.bulk
          ? `${deleteConfirm.count}개의 락커를 삭제하시겠습니까?${deleteConfirm.occupiedCount > 0 ? ` (${deleteConfirm.occupiedCount}개 사용중)` : ''}`
          : `${deleteConfirm?.number}번 락커를 삭제하시겠습니까?${deleteConfirm?.memberName ? ` (${deleteConfirm.memberName} 배정 중)` : ''}`
        }
        onConfirm={() => {
          if (deleteConfirm?.bulk) {
            handleBulkDelete();
          } else {
            deleteLocker(deleteConfirm.id);
          }
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default Lockers;
